import { formatBytes } from "@/lib/formatters";
import { useContext, useEffect, useRef, useState, type ClipboardEvent } from "react";
import { Bug, CircleAlert, CircleCheck, CircleHelp, Copy, Lightbulb, LifeBuoy, Paperclip, Send, X, type LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { Textarea } from "@/components/ui/textarea";
import { AuthContext } from "@/contexts/auth-context";
import {
  ATTACHMENT_ACCEPT,
  MAX_ATTACHMENTS,
  MAX_ATTACHMENTS_BYTES,
  buildErrorReport,
  formatReportText,
  sendErrorReport,
  type ErrorReportResult,
} from "@/lib/error-report";

interface ReportProblemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** O que a pessoa viu (a frase do aviso). Sem isto, é um relatório livre e a mensagem é obrigatória. */
  title?: string;
  /** O pormenor técnico, se houver. */
  message?: string;
  /** Em que parte da app. */
  area?: string;
  context?: Record<string, unknown>;
}

type Status = "idle" | "sending" | "sent" | "failed";

/** O que a pessoa quer dizer à equipa. Só muda o título do pedido e a dica do campo: o circuito é o mesmo. */
type Kind = "problem" | "suggestion" | "question";
const KINDS: { id: Kind; label: string; icon: LucideIcon; subject: string; hint: string }[] = [
  { id: "problem", label: "Problema", icon: Bug, subject: "Problema reportado pela pessoa", hint: "Descreve o problema com as tuas palavras. Podes colar aqui uma captura de ecrã." },
  { id: "suggestion", label: "Sugestão", icon: Lightbulb, subject: "Sugestão enviada pela pessoa", hint: "Conta-nos a ideia: o que gostavas que fosse diferente e porquê." },
  { id: "question", label: "Dúvida", icon: CircleHelp, subject: "Dúvida enviada pela pessoa", hint: "O que não consegues perceber ou encontrar?" },
];

const MIN_FREE_NOTE = 5;


/**
 * «Reportar ou sugerir»: a pessoa escreve o que se passa (ou o que sugere), junta o que ajude
 * (uma captura de ecrã, o ficheiro que deu erro) e um clique manda tudo por email
 * à equipa — com o registo técnico da página e quem é. A equipa responde por email.
 * Se o email não sair, o relatório fica à mão para copiar: quem reporta nunca
 * fica sem forma de o fazer chegar.
 */
export const ReportProblemDialog = ({
  open,
  onOpenChange,
  title,
  message,
  area,
  context,
}: ReportProblemDialogProps) => {
  const user = useContext(AuthContext)?.user;
  const fileInput = useRef<HTMLInputElement>(null);
  const [note, setNote] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [status, setStatus] = useState<Status>("idle");
  const [result, setResult] = useState<ErrorReportResult | null>(null);
  const [fallbackText, setFallbackText] = useState("");
  const [copied, setCopied] = useState(false);
  const [kind, setKind] = useState<Kind>("problem");
  const kindDef = KINDS.find((k) => k.id === kind) ?? KINDS[0];

  // Cada abertura é um relatório novo.
  useEffect(() => {
    if (open) {
      setNote("");
      setFiles([]);
      setFileError(null);
      setStatus("idle");
      setResult(null);
      setFallbackText("");
      setCopied(false);
      setKind("problem");
    }
  }, [open]);

  const isFree = !title;
  const canSend = status !== "sending" && (!isFree || note.trim().length >= MIN_FREE_NOTE);

  const addFiles = (incoming: File[]) => {
    if (incoming.length === 0) return;
    const next = [...files, ...incoming];
    const total = next.reduce((sum, f) => sum + f.size, 0);
    if (next.length > MAX_ATTACHMENTS) {
      setFileError(`Podes juntar no máximo ${MAX_ATTACHMENTS} ficheiros.`);
    } else if (total > MAX_ATTACHMENTS_BYTES) {
      setFileError(
        `Os ficheiros são grandes de mais (${formatBytes(total)}). O limite, no total, é ${formatBytes(MAX_ATTACHMENTS_BYTES)}.`,
      );
    } else {
      setFileError(null);
      setFiles(next);
    }
  };

  // Colar uma captura de ecrã (Ctrl+V) na mensagem junta-a como anexo.
  const onPaste = (event: ClipboardEvent<HTMLTextAreaElement>) => {
    const images = Array.from(event.clipboardData.files).filter((f) => f.type.startsWith("image/"));
    if (images.length > 0) {
      event.preventDefault();
      addFiles(images);
    }
  };

  const send = async () => {
    const payload = buildErrorReport({
      title: title ?? kindDef.subject,
      message,
      note,
      area,
      context,
    });
    setFallbackText(formatReportText(payload));
    setStatus("sending");
    try {
      const response = await sendErrorReport(payload, files);
      setResult(response);
      setStatus(response.emailed ? "sent" : "failed");
    } catch {
      setStatus("failed");
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(fallbackText);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] gap-0 overflow-hidden p-0 sm:max-w-lg">
        <div className="max-h-[calc(90vh-4.5rem)] space-y-5 overflow-y-auto overscroll-contain px-6 pb-5 pt-6">
        <DialogHeader className="flex-row items-start gap-4 space-y-0 text-left">
          {/* O ícone do pedido: um quadrado neutro, como os restantes cabeçalhos (a cor fica para as ações). */}
          <span
            aria-hidden
            className={cn(
              "grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-foreground/[0.06]",
              status === "sent" ? "text-emerald-600 dark:text-emerald-400" : "text-foreground",
            )}
          >
            {status === "sent" ? (
              <CircleCheck className="h-5 w-5" strokeWidth={1.75} />
            ) : (
              <LifeBuoy className="h-5 w-5" strokeWidth={1.75} />
            )}
          </span>
          <div className="min-w-0 flex-1 space-y-1 pr-6">
            <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">Pedido à equipa</p>
            <DialogTitle className="text-xl font-light leading-tight tracking-tight">
              {status === "sent"
                ? "Enviado à equipa"
                : isFree
                  ? "Reportar ou sugerir"
                  : "Enviar este problema à equipa"}
            </DialogTitle>
            <DialogDescription className="text-[13px] leading-relaxed">
              {status === "sent"
                ? "Recebemos o teu relatório. A equipa responde-te por e-mail."
                : "Conta-nos o que se passa ou o que gostavas de ver melhorado. A equipa recebe o pedido por e-mail, com a página onde estavas e o registo técnico dela, e responde-te."}
            </DialogDescription>
          </div>
        </DialogHeader>

        {status === "sent" && (
          <div
            role="status"
            className="flex gap-3 rounded-xl border border-emerald-600/40 px-4 py-3 dark:border-emerald-400/40"
          >
            <CircleCheck
              className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400"
              strokeWidth={1.75}
              aria-hidden
            />
            <div>
              <p className="text-[13px] leading-relaxed">
                Número do relatório:{" "}
                <strong className="font-medium tabular-nums">{result?.report_id}</strong>.
                {user?.email && <> A resposta chega ao teu e-mail ({user.email}).</>}
              </p>
              {result?.detail && (
                <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">{result.detail}</p>
              )}
            </div>
          </div>
        )}

        {status === "failed" && (
          <div role="alert" className="space-y-3 rounded-xl border border-destructive/40 px-4 py-3">
            <div className="flex gap-3">
              <CircleAlert
                className="mt-0.5 h-4 w-4 shrink-0 text-destructive"
                strokeWidth={1.75}
                aria-hidden
              />
              <div className="space-y-1">
                <p className="text-sm font-medium">Não consegui enviar o e-mail</p>
                <p className="text-[13px] leading-relaxed text-muted-foreground">
                  {result?.report_id
                    ? `O relatório ${result.report_id} ficou guardado no servidor, mas o e-mail não saiu. `
                    : "Não consegui falar com o servidor. "}
                  Copia o relatório e envia-o por e-mail à equipa
                  {files.length > 0 ? ", juntando os ficheiros à mão" : ""}.
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-8 gap-1.5 rounded-lg text-xs"
              onClick={copy}
            >
              <Copy className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
              {copied ? "Copiado!" : "Copiar relatório"}
            </Button>
          </div>
        )}

        {status !== "sent" && (
          <>
            {isFree && (
              <div className="space-y-2">
                <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground" id="report-kind-label">Tipo de pedido</p>
                <div role="radiogroup" aria-labelledby="report-kind-label" className="grid grid-cols-3 gap-1.5">
                  {KINDS.map(({ id, label, icon: Icon }) => (
                    <button
                      key={id}
                      type="button"
                      role="radio"
                      aria-checked={kind === id}
                      disabled={status === "sending"}
                      onClick={() => setKind(id)}
                      className={cn(
                        "flex h-9 items-center justify-center gap-2 rounded-lg border text-[13px] transition-colors",
                        "focus-visible:border-primary focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/10",
                        kind === id
                          ? "border-border bg-foreground/[0.06] font-medium text-foreground"
                          : "border-border/60 text-muted-foreground hover:bg-foreground/[0.035] hover:text-foreground",
                      )}
                    >
                      <Icon className="h-4 w-4" strokeWidth={1.75} aria-hidden />
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="report-note" className="text-sm font-medium">
                {isFree ? "O que se passa ou o que sugeres?" : "O que estava a fazer? (opcional)"}
              </Label>
              <Textarea
                id="report-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                onPaste={onPaste}
                placeholder={
                  isFree
                    ? kindDef.hint
                    : "Por exemplo: estava a carregar o ficheiro do cliente e apareceu este aviso."
                }
                maxLength={2000}
                rows={4}
                disabled={status === "sending"}
              />
            </div>

            <div className="space-y-2">
              <input
                ref={fileInput}
                type="file"
                multiple
                accept={ATTACHMENT_ACCEPT}
                className="sr-only"
                onChange={(e) => {
                  addFiles(Array.from(e.target.files ?? []));
                  e.target.value = "";
                }}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-9 gap-1.5 rounded-lg text-[13px]"
                onClick={() => fileInput.current?.click()}
                disabled={status === "sending" || files.length >= MAX_ATTACHMENTS}
              >
                <Paperclip className="h-4 w-4" strokeWidth={1.75} aria-hidden />
                Juntar ficheiros
              </Button>
              <p className="text-xs text-muted-foreground">
                Até {MAX_ATTACHMENTS} ficheiros, {formatBytes(MAX_ATTACHMENTS_BYTES)} no total
                (capturas de ecrã, PDF, Excel, CSV). Vão por e-mail para a equipa.
              </p>
              {fileError && (
                <p role="alert" className="text-[13px] text-destructive">
                  {fileError}
                </p>
              )}
              {files.length > 0 && (
                <ul className="space-y-1.5">
                  {files.map((file, index) => (
                    <li
                      key={`${file.name}-${index}`}
                      className="flex items-center gap-2 rounded-lg border border-border/60 px-3 py-1.5 text-[13px]"
                    >
                      <Paperclip
                        className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                        strokeWidth={1.75}
                        aria-hidden
                      />
                      <span className="min-w-0 flex-1 truncate">{file.name}</span>
                      <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                        {formatBytes(file.size)}
                      </span>
                      <button
                        type="button"
                        onClick={() => setFiles(files.filter((_, i) => i !== index))}
                        disabled={status === "sending"}
                        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-foreground/6 hover:text-foreground"
                      >
                        <X className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
                        <span className="sr-only">Tirar {file.name}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* O que vai com o pedido, à vista: ninguém fica a perguntar-se o que a equipa recebe. */}
            <dl className="divide-y divide-border/50 rounded-xl border border-border/60 text-[13px]">
              <div className="flex items-baseline justify-between gap-4 px-3.5 py-2.5">
                <dt className="text-muted-foreground">Página</dt>
                <dd className="min-w-0 truncate tabular-nums">{area ?? (typeof window !== "undefined" ? window.location.pathname : "")}</dd>
              </div>
              {user?.email && (
                <div className="flex items-baseline justify-between gap-4 px-3.5 py-2.5">
                  <dt className="text-muted-foreground">Resposta para</dt>
                  <dd className="min-w-0 truncate">{user.email}</dd>
                </div>
              )}
            </dl>
          </>
        )}
        </div>

        <DialogFooter className="gap-2 border-t border-border/60 bg-foreground/[0.02] px-6 py-4 sm:gap-2">
          {status === "sent" ? (
            <Button onClick={() => onOpenChange(false)}>Fechar</Button>
          ) : (
            <>
              <Button
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={status === "sending"}
              >
                Cancelar
              </Button>
              <Button onClick={send} disabled={!canSend} className="gap-1.5">
                <Send className="h-4 w-4" strokeWidth={1.75} aria-hidden />
                {status === "sending"
                  ? "A enviar…"
                  : status === "failed"
                    ? "Tentar outra vez"
                    : "Enviar à equipa"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
